import { USER_ROLES, USER_ROLE_VALUES } from '@cult/domain';
import { z } from 'zod';
import { CursorPageSchema, CursorQuerySchema, OffsetQuerySchema, SortOrderSchema, qField } from './common';
import { IdeaStatusSchema, PublicIdeaSchema } from './ideas';

export const STATISTICS_SERIES = {
  SUGGESTED: 'suggested',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PRAISE: 'praise',
} as const;
export type StatisticsSeries = (typeof STATISTICS_SERIES)[keyof typeof STATISTICS_SERIES];
export const STATISTICS_SERIES_VALUES = Object.values(STATISTICS_SERIES) as [StatisticsSeries, ...StatisticsSeries[]];
export const StatisticsSeriesSchema = z.enum(STATISTICS_SERIES_VALUES);

export const STATISTICS_PERIODS = {
  WEEK: '7d',
  MONTH: '30d',
  QUARTER: '90d',
} as const;
export type StatisticsPeriod = (typeof STATISTICS_PERIODS)[keyof typeof STATISTICS_PERIODS];
export const STATISTICS_PERIOD_VALUES = Object.values(STATISTICS_PERIODS) as [StatisticsPeriod, ...StatisticsPeriod[]];
export const STATISTICS_PERIOD_DAYS: Record<StatisticsPeriod, number> = {
  [STATISTICS_PERIODS.WEEK]: 7,
  [STATISTICS_PERIODS.MONTH]: 30,
  [STATISTICS_PERIODS.QUARTER]: 90,
};
export const DEFAULT_STATISTICS_PERIOD: StatisticsPeriod = STATISTICS_PERIODS.QUARTER;

export const ActivityBucketSchema = z.object({
  date: z.string(),
  [STATISTICS_SERIES.SUGGESTED]: z.number().int().optional(),
  [STATISTICS_SERIES.APPROVED]: z.number().int().optional(),
  [STATISTICS_SERIES.REJECTED]: z.number().int().optional(),
  [STATISTICS_SERIES.PRAISE]: z.number().int().optional(),
});
export type ActivityBucket = z.infer<typeof ActivityBucketSchema>;

export const SeriesSummarySchema = z.object({
  key: StatisticsSeriesSchema,
  total: z.number().int(),
});
export type SeriesSummary = z.infer<typeof SeriesSummarySchema>;

export const ActivitySeriesSchema = z.object({
  buckets: z.array(ActivityBucketSchema),
  summaries: z.array(SeriesSummarySchema),
});
export type ActivitySeries = z.infer<typeof ActivitySeriesSchema>;

export const StatusBreakdownSliceSchema = z.object({
  status: IdeaStatusSchema,
  count: z.number().int(),
});
export type StatusBreakdownSlice = z.infer<typeof StatusBreakdownSliceSchema>;

export const StatusBreakdownSchema = z.object({
  total: z.number().int(),
  slices: z.array(StatusBreakdownSliceSchema),
});
export type StatusBreakdown = z.infer<typeof StatusBreakdownSchema>;

export const MEMBER_STAT_SORTS = {
  SUGGESTED: 'suggested',
  APPROVED: 'approved',
  PRAISE: 'praise',
} as const;
export type MemberStatSort = (typeof MEMBER_STAT_SORTS)[keyof typeof MEMBER_STAT_SORTS];
export const MEMBER_STAT_SORT_VALUES = Object.values(MEMBER_STAT_SORTS) as [MemberStatSort, ...MemberStatSort[]];
export const MemberStatSortSchema = z.enum(MEMBER_STAT_SORT_VALUES);

// super_admin is excluded: it's an env-bootstrapped deploy account, not an org segment.
export const STATISTICS_ROLES = [
  USER_ROLES.MEMBER,
  USER_ROLES.IDEA_MANAGER,
  USER_ROLES.MODERATOR,
  USER_ROLES.ADMIN,
] as const;
export type StatisticsRole = (typeof STATISTICS_ROLES)[number];
export const StatisticsRoleSchema = z.enum(STATISTICS_ROLES);

export const MemberStatsQuerySchema = OffsetQuerySchema.extend({
  q: qField,
  role: StatisticsRoleSchema.optional(),
  sort: MemberStatSortSchema.optional(),
  order: SortOrderSchema.optional(),
  period: z.enum(STATISTICS_PERIOD_VALUES).default(DEFAULT_STATISTICS_PERIOD),
});
export type MemberStatsQuery = z.infer<typeof MemberStatsQuerySchema>;

export const MemberStatRowSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  suggested: z.number().int(),
  approved: z.number().int(),
  praise: z.number().int(),
});
export type MemberStatRow = z.infer<typeof MemberStatRowSchema>;

export const MemberStatsResponseSchema = z.object({
  members: z.array(MemberStatRowSchema),
  total: z.number().int(),
});
export type MemberStatsResponse = z.infer<typeof MemberStatsResponseSchema>;

export const MemberActivityQuerySchema = z.object({
  period: z.enum(STATISTICS_PERIOD_VALUES).default(DEFAULT_STATISTICS_PERIOD),
});
export type MemberActivityQuery = z.infer<typeof MemberActivityQuerySchema>;

export const MemberActivityItemSchema = z.object({
  id: z.string().uuid(),
  number: z.number().int(),
  title: z.string(),
});
export type MemberActivityItem = z.infer<typeof MemberActivityItemSchema>;

export const MemberActivityResponseSchema = z.object({
  ideas: z.array(MemberActivityItemSchema),
  praises: z.array(MemberActivityItemSchema),
});
export type MemberActivityResponse = z.infer<typeof MemberActivityResponseSchema>;

export const ReferenceBreakdownSliceSchema = z.object({
  slug: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  count: z.number().int(),
});
export type ReferenceBreakdownSlice = z.infer<typeof ReferenceBreakdownSliceSchema>;

export const ReferenceBreakdownSchema = z.object({
  total: z.number().int(),
  slices: z.array(ReferenceBreakdownSliceSchema),
});
export type ReferenceBreakdown = z.infer<typeof ReferenceBreakdownSchema>;

export const StatisticsOverviewQuerySchema = z.object({
  period: z.enum(STATISTICS_PERIOD_VALUES).default(DEFAULT_STATISTICS_PERIOD),
});
export type StatisticsOverviewQuery = z.infer<typeof StatisticsOverviewQuerySchema>;

export const StatisticsOverviewResponseSchema = z.object({
  period: z.enum(STATISTICS_PERIOD_VALUES),
  ideas: ActivitySeriesSchema,
  praises: ActivitySeriesSchema,
  statusBreakdown: StatusBreakdownSchema,
  praisesByValue: ReferenceBreakdownSchema,
  praisesByFramework: ReferenceBreakdownSchema,
});
export type StatisticsOverviewResponse = z.infer<typeof StatisticsOverviewResponseSchema>;

export const AuthorIdeasQuerySchema = CursorQuerySchema.extend({
  period: z.enum(STATISTICS_PERIOD_VALUES).default(DEFAULT_STATISTICS_PERIOD),
  status: IdeaStatusSchema.optional(),
  q: qField,
});
export type AuthorIdeasQuery = z.infer<typeof AuthorIdeasQuerySchema>;

export const AuthorProfileSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  role: z.enum(USER_ROLE_VALUES),
  joinedAt: z.string(),
});
export type AuthorProfile = z.infer<typeof AuthorProfileSchema>;

export const AuthorIdeasSummarySchema = z.object({
  totalIdeas: z.number().int().nonnegative(),
  approved: z.number().int().nonnegative(),
});
export type AuthorIdeasSummary = z.infer<typeof AuthorIdeasSummarySchema>;

// author + summary ride along with the first page only (no cursor); null while paging.
export const AuthorIdeasResponseSchema = CursorPageSchema.extend({
  author: AuthorProfileSchema.nullable(),
  summary: AuthorIdeasSummarySchema.nullable(),
  ideas: z.array(PublicIdeaSchema),
});
export type AuthorIdeasResponse = z.infer<typeof AuthorIdeasResponseSchema>;
